import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignedIn, useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';
import { refreshMeRole } from '../hooks/useMeRole';

/**
 * JoinMarket: enter market code to become a vendor/owner.
 * Used at /vendor/login (code empty) and /signup/:marketCode (code pre-filled from URL).
 */
export default function JoinMarket({ preFilledCode = '' }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState(preFilledCode || '');
  useEffect(() => {
    if (preFilledCode) setCode(preFilledCode);
  }, [preFilledCode]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = (code || '').trim();
    if (!trimmed) {
      setError('Enter a market code.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await api('/api/vendors/assign-code', {
        getToken,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.message || `Error ${res.status}`);
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
      refreshMeRole();
      // Owners are redirected to /owner by OwnerGuard; vendors land on store profile.
      navigate(data.isOwner ? '/owner' : '/profile', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SignedIn>
      <div className="flex flex-col gap-6">
        <h1 className="text-3xl font-semibold text-center">JOIN YOUR MARKET</h1>
        <p className="text-lg text-slate-600 dark:text-slate-400 text-center">
          Enter the market code you received to join a flea market.
        </p>
        {error && (
          <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
            {error}
          </p>
        )}
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <label className="flex flex-col gap-2">
            <span className="text-lg font-medium">Market code</span>
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Paste or type the market code"
              rows={4}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-4 text-xl font-mono resize-y min-h-[120px]"
              disabled={submitting}
            />
          </label>
          <button
            type="submit"
            disabled={submitting}
            className="h-16 text-2xl w-full rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Joining…' : 'Join market'}
          </button>
        </form>
      </div>
    </SignedIn>
  );
}
